import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Leaf } from 'lucide-react';
import type { GardenBridge } from '../game/GardenScene';
import { GardenLandscape } from './GardenLandscape';

export function Garden({ bridge }: { bridge: GardenBridge }) {
  const host = useRef<HTMLDivElement>(null);
  const scene = useRef<import('../game/GardenScene').GardenScene | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);
  const { t } = useTranslation();
  const latest = useRef(bridge); latest.current = bridge;
  useEffect(() => { scene.current?.setBridge({ ...bridge, onReady: () => setLoaded(true) }); }, [bridge]);
  useEffect(() => {
    let disposed = false;
    let game: import('phaser').Game | undefined;
    let observer: ResizeObserver | undefined;
    void (async () => {
      try {
        const [Phaser, { GardenScene }] = await Promise.all([import('phaser'), import('../game/GardenScene'), document.fonts.ready]);
        if (disposed || !host.current) return;
        const ratio = Math.min(3, Math.max(1, window.devicePixelRatio || 1));
        scene.current = new GardenScene({ ...latest.current, onReady: () => setLoaded(true) }, ratio);
        game = new Phaser.default.Game({ type: Phaser.default.AUTO, parent: host.current, transparent: true, antialias: true, render: { roundPixels: false }, scale: { mode: Phaser.default.Scale.NONE, width: Math.round(host.current.clientWidth * ratio), height: Math.round(host.current.clientHeight * ratio), zoom: 1 / ratio, autoRound: false }, scene: scene.current, audio: { noAudio: true }, fps: { target: 40, forceSetTimeOut: false } });
        game.canvas.setAttribute('aria-hidden', 'true');
        game.canvas.dataset.pixelRatio = String(ratio);
        observer = new ResizeObserver(() => {
          if (disposed || !game?.isBooted || !host.current) return;
          const width = Math.round(host.current.clientWidth * ratio), height = Math.round(host.current.clientHeight * ratio);
          if (width > 0 && height > 0 && (game.scale.width !== width || game.scale.height !== height)) game.scale.resize(width, height);
        });
        observer.observe(host.current);
      } catch { if (!disposed) setFailed(true); }
    })();
    return () => { disposed = true; observer?.disconnect(); game?.destroy(true); scene.current = null; };
  }, []);
  return <div className={`garden-render ${bridge.you ? 'has-guest' : ''}`} role="img" aria-label={t(bridge.you ? 'garden.canvasLabel' : 'garden.preview')} data-floor-opacity={bridge.floorOpacity}>
    <GardenLandscape mood={bridge.mood} reducedMotion={bridge.reducedMotion} />
    <div ref={host} className="phaser-host" data-testid="garden-canvas" />
    {!loaded && <div className="garden-loading" role="status"><Leaf size={29} /><span>{t(failed ? 'garden.sceneFailed' : 'garden.sceneLoading')}</span></div>}
  </div>;
}
